import { FC, memo, useMemo } from 'react'

import { FormPhoneInput } from 'clickNgo/components/formPhoneInput'
import { Region } from 'clickNgo/configs/geo'
import { PartnerFields, PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { FormInput, useGetFormDataField } from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { FieldGroup } from 'shared/components/fieldGroup/FieldGroup'

import { partnerBasicInfoTranslation } from '../../translation'

import { ICommonBlockProps } from './models'

import styles from './CommonBlock.module.scss'

export const CommonBlock: FC<ICommonBlockProps> = memo(
  ({
    communicationName,
    isDisabled,
    isDisabledByRegion,
    isReadOnly,
    storeNumber,
    email,
    url,
    taxId,
    phoneCountryCode,
    phone,
    modifiedValues,
    region,
    formKeys,
    isCommunicationNameRequired,
    isEmailRequired,
    isPhoneCodeRequired,
    isPhoneNumberRequired,
    isStoreNumberRequired,
    isTaxIdRequired,
    isUrlRequired,
    hasActiveAgreement,
    onCommunicationNameValidate,
    onEmailValidate,
    onPhoneCodeValidate,
    onPhoneNumberValidate,
    onStoreNumberValidate,
    onTaxIdValidate,
    onURLValidate,
    isNewPartner,
  }) => {
    const [country] = useGetFormDataField<string>(formKeys.basic, PartnerFields.country.field)
    const [hierarchyType] = useGetFormDataField<PartnerHierarchyType>(
      formKeys.basic,
      PartnerFields.hierarchyType.field
    )

    const isTaxIdReadOnly = useMemo(() => {
      const isBranchOrOnline = [PartnerHierarchyType.BRANCH, PartnerHierarchyType.ONLINE].includes(
        hierarchyType
      )

      const isEMEAOrAPJRegion = [Region.EMEA, Region.APJ].includes(region)
      if (isNewPartner) {
        return isBranchOrOnline || isReadOnly
      }

      return (isEMEAOrAPJRegion && isBranchOrOnline && !hasActiveAgreement) || isReadOnly
    }, [hasActiveAgreement, hierarchyType, isNewPartner, isReadOnly, region])

    const isTaxIdVisible = useMemo(() => {
      const isBranchOrOnline = [PartnerHierarchyType.BRANCH, PartnerHierarchyType.ONLINE].includes(
        hierarchyType
      )
      const isUSA = country === 'USA'

      return !isBranchOrOnline || !isUSA
    }, [country, hierarchyType])

    return (
      <FieldGroup title={partnerBasicInfoTranslation.content.blockTitles.commonBlock}>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.taxId.externalField)}
        >
          <FormInput
            field={PartnerFields.taxId.field}
            label={PartnerFields.taxId.label}
            formKey={formKeys.basic}
            defaultValue={taxId}
            onValidate={onTaxIdValidate}
            isDisabled={isDisabled}
            isReadOnly={isTaxIdReadOnly}
            isRequired={isTaxIdRequired}
            isVisible={isTaxIdVisible}
            validationTooltipClassName={styles.taxIdValidationTooltip}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            modifiedValues,
            PartnerFields.communicationName.externalField
          )}
        >
          <FormInput
            formKey={formKeys.basic}
            field={PartnerFields.communicationName.field}
            label={PartnerFields.communicationName.label}
            defaultValue={communicationName}
            onValidate={onCommunicationNameValidate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isCommunicationNameRequired}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.email.externalField)}
        >
          <FormInput
            field={PartnerFields.email.field}
            label={PartnerFields.email.label}
            formKey={formKeys.basic}
            defaultValue={email}
            onValidate={onEmailValidate}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isEmailRequired}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={
            wasFieldModified(modifiedValues, PartnerFields.phone.externalField) ||
            wasFieldModified(modifiedValues, PartnerFields.phoneCountryCode.externalField)
          }
        >
          <FormPhoneInput
            formKey={formKeys.basic}
            phoneCodeValue={phoneCountryCode}
            phoneNumberValue={phone}
            phoneCodeFieldName={PartnerFields.phoneCountryCode.field}
            phoneNumberFieldName={PartnerFields.phone.field}
            label={PartnerFields.phone.label}
            isDisabled={isDisabled || isDisabledByRegion}
            isReadOnly={isReadOnly}
            isPhoneCodeRequired={isPhoneCodeRequired}
            isPhoneNumberRequired={isPhoneNumberRequired}
            onPhoneCodeValidate={onPhoneCodeValidate}
            onPhoneNumberValidate={onPhoneNumberValidate}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.storeNumber.externalField)}
        >
          <FormInput
            field={PartnerFields.storeNumber.field}
            label={PartnerFields.storeNumber.label}
            formKey={formKeys.basic}
            defaultValue={storeNumber}
            onValidate={onStoreNumberValidate}
            isReadOnly={isReadOnly}
            isRequired={isStoreNumberRequired}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(modifiedValues, PartnerFields.url.externalField)}
        >
          <FormInput
            field={PartnerFields.url.field}
            label={PartnerFields.url.label}
            formKey={formKeys.basic}
            defaultValue={url}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            onValidate={onURLValidate}
            isRequired={isUrlRequired}
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
    )
  }
)

CommonBlock.displayName = 'CommonBlock'
